import type { BlendModifier } from "../../blend-reader/types/BlendModifier.ts";
import {
  identityProvenance,
  provenanceFromSourceMap,
} from "../../modeling/provenance.ts";
import {
  buildTopologyWithExplicitEdges,
  concatTopology,
} from "../../modeling/topology.ts";
import {
  asSharedTriangleIndices,
  EMPTY_SHARED_TRIANGLE_INDICES,
  type PolygonTopology,
} from "../../modeling/types.ts";
import { projectVertexGroupThroughProvenance } from "./helpers.ts";
import type {
  GeometryGroup,
  ModifierEvaluationInput,
  ModifierGeometry,
} from "./modifierTypes.ts";

// ---------------------------------------------------------------------------
// Minimal column-major 4×4 matrix / vec3 helpers (no Three.js dependency)
// ---------------------------------------------------------------------------

function transformPoint(
  m: number[],
  x: number,
  y: number,
  z: number,
): [number, number, number] {
  return [
    m[0] * x + m[4] * y + m[8] * z + m[12],
    m[1] * x + m[5] * y + m[9] * z + m[13],
    m[2] * x + m[6] * y + m[10] * z + m[14],
  ];
}

function transformDir(
  m: number[],
  x: number,
  y: number,
  z: number,
): [number, number, number] {
  return [
    m[0] * x + m[4] * y + m[8] * z,
    m[1] * x + m[5] * y + m[9] * z,
    m[2] * x + m[6] * y + m[10] * z,
  ];
}

/** Build a column-major 4×4 matrix from an orthonormal basis + translation. */
function mat4FromBasis(
  tx: number,
  ty: number,
  tz: number,
  bx: number,
  by: number,
  bz: number,
  nx: number,
  ny: number,
  nz: number,
  px: number,
  py: number,
  pz: number,
): number[] {
  // prettier-ignore
  return [tx, ty, tz, 0, bx, by, bz, 0, nx, ny, nz, 0, px, py, pz, 1];
}

/** Build a column-major 4×4 translation-only matrix. */
function mat4Translation(x: number, y: number, z: number): number[] {
  return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, x, y, z, 1];
}

// ---------------------------------------------------------------------------
// Deterministic pseudo-random number generator (Wang hash)
// Produces a stable float in [0, 1) from an integer seed so scatter placements
// are reproducible across re-renders without needing an external seed prop.
// ---------------------------------------------------------------------------

function seededRand(seed: number): number {
  // Murmur3-style finalizer. Adding 1 before multiplying avoids the zero fixed
  // point (seededRand(0) would otherwise return 0, causing the first element
  // to always land at t=0 / vertex 0 in scatter mode).
  let h = Math.imul((seed + 1) | 0, 0x9e3779b9) | 0;
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b) | 0;
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35) | 0;
  return ((h ^ (h >>> 16)) >>> 0) / 0x100000000;
}

// ---------------------------------------------------------------------------
// Placement point extraction
// ---------------------------------------------------------------------------

interface PlacementPoints {
  /** Flat [x,y,z, x,y,z, ...] placement positions. */
  positions: Float32Array;
  /** Flat [nx,ny,nz, ...] surface normals (null when orientation !== "normal"). */
  normals: Float32Array | null;
  count: number;
}

/** Compute face normals for all faces in a topology. Returns flat Float32Array (stride 3). */
function computeFaceNormals(
  positions: Float32Array,
  topo: PolygonTopology,
): Float32Array {
  const faceCount = topo.offsets.length - 1;
  const normals = new Float32Array(faceCount * 3);

  for (let f = 0; f < faceCount; f++) {
    const start = topo.offsets[f];
    const end = topo.offsets[f + 1];
    if (end - start < 3) continue;

    const v0 = topo.corners[start];
    const v1 = topo.corners[start + 1];
    const v2 = topo.corners[start + 2];

    const ax = positions[v1 * 3] - positions[v0 * 3];
    const ay = positions[v1 * 3 + 1] - positions[v0 * 3 + 1];
    const az = positions[v1 * 3 + 2] - positions[v0 * 3 + 2];
    const bx = positions[v2 * 3] - positions[v0 * 3];
    const by = positions[v2 * 3 + 1] - positions[v0 * 3 + 1];
    const bz = positions[v2 * 3 + 2] - positions[v0 * 3 + 2];

    let nx = ay * bz - az * by;
    let ny = az * bx - ax * bz;
    let nz = ax * by - ay * bx;
    const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
    if (len > 1e-10) {
      nx /= len;
      ny /= len;
      nz /= len;
    }

    normals[f * 3] = nx;
    normals[f * 3 + 1] = ny;
    normals[f * 3 + 2] = nz;
  }

  return normals;
}

/** Compute per-vertex normals by averaging adjacent face normals. */
function computeVertexNormals(
  positions: Float32Array,
  topo: PolygonTopology,
  faceNormals: Float32Array,
): Float32Array {
  const vertexCount = positions.length / 3;
  const normals = new Float32Array(vertexCount * 3);
  const faceCount = topo.offsets.length - 1;

  for (let f = 0; f < faceCount; f++) {
    const start = topo.offsets[f];
    const end = topo.offsets[f + 1];
    const fnx = faceNormals[f * 3];
    const fny = faceNormals[f * 3 + 1];
    const fnz = faceNormals[f * 3 + 2];
    for (let c = start; c < end; c++) {
      const v = topo.corners[c];
      normals[v * 3] += fnx;
      normals[v * 3 + 1] += fny;
      normals[v * 3 + 2] += fnz;
    }
  }

  // Normalize
  for (let v = 0; v < vertexCount; v++) {
    const i = v * 3;
    const nx = normals[i],
      ny = normals[i + 1],
      nz = normals[i + 2];
    const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
    if (len > 1e-10) {
      normals[i] = nx / len;
      normals[i + 1] = ny / len;
      normals[i + 2] = nz / len;
    } else {
      normals[i + 2] = 1; // fallback: up
    }
  }

  return normals;
}

/** Build an edge→faces map from topology. Returns a Map<edgeIndex, faceIndex[]>. */
function buildEdgeFaceMap(topo: PolygonTopology): Map<number, number[]> {
  const map = new Map<number, number[]>();
  const faceCount = topo.offsets.length - 1;
  for (let f = 0; f < faceCount; f++) {
    const start = topo.offsets[f];
    const end = topo.offsets[f + 1];
    for (let c = start; c < end; c++) {
      const ei = topo.cornerEdges[c];
      let list = map.get(ei);
      if (!list) {
        list = [];
        map.set(ei, list);
      }
      list.push(f);
    }
  }
  return map;
}

function extractVertexPoints(
  geo: ModifierGeometry,
  limit: number,
  needNormals: boolean,
  allowed?: Set<number>,
): PlacementPoints {
  const vertexCount = geo.positions.length / 3;

  // Candidate vertex indices — either every vertex or only the filtered set.
  // Stride is applied AFTER filtering so a 10-vertex group under a 64 limit
  // yields 10 instances, not 1.
  let candidates: number[] | null = null;
  if (allowed) {
    candidates = [];
    for (let v = 0; v < vertexCount; v++) {
      if (allowed.has(v)) candidates.push(v);
    }
  }
  const candidateCount = candidates ? candidates.length : vertexCount;
  const count = Math.min(candidateCount, limit);
  const stride =
    candidateCount <= limit ? 1 : Math.ceil(candidateCount / limit);

  const positions = new Float32Array(count * 3);
  let normals: Float32Array | null = null;
  let vertexNormals: Float32Array | null = null;

  if (needNormals && geo.topology) {
    const faceNormals = computeFaceNormals(geo.positions, geo.topology);
    vertexNormals = computeVertexNormals(
      geo.positions,
      geo.topology,
      faceNormals,
    );
  }

  if (needNormals) {
    normals = new Float32Array(count * 3);
  }

  for (let i = 0; i < count; i++) {
    const vi = candidates ? candidates[i * stride] : i * stride;
    positions[i * 3] = geo.positions[vi * 3];
    positions[i * 3 + 1] = geo.positions[vi * 3 + 1];
    positions[i * 3 + 2] = geo.positions[vi * 3 + 2];
    if (normals && vertexNormals) {
      normals[i * 3] = vertexNormals[vi * 3];
      normals[i * 3 + 1] = vertexNormals[vi * 3 + 1];
      normals[i * 3 + 2] = vertexNormals[vi * 3 + 2];
    }
  }

  return { positions, normals, count };
}

function extractEdgePoints(
  geo: ModifierGeometry,
  limit: number,
  needNormals: boolean,
  scatter = false,
  perElement = 1,
  allowed?: Set<number>,
): PlacementPoints {
  const topo = geo.topology;
  if (!topo) return { positions: new Float32Array(0), normals: null, count: 0 };

  const edgeCount = topo.edges.length / 2;

  // Candidate edge indices — when filtering by vertex group, keep only edges
  // whose BOTH endpoints are in the set. This is what makes "instantiate along
  // a loop" work: a selected edge loop captured as a vertex group has both
  // endpoints of every loop edge inside the group.
  let candidates: number[] | null = null;
  if (allowed) {
    candidates = [];
    for (let e = 0; e < edgeCount; e++) {
      const v0 = topo.edges[e * 2];
      const v1 = topo.edges[e * 2 + 1];
      if (allowed.has(v0) && allowed.has(v1)) candidates.push(e);
    }
  }
  const candidateCount = candidates ? candidates.length : edgeCount;
  const elementCount = Math.min(candidateCount, limit);
  const stride =
    candidateCount <= limit ? 1 : Math.ceil(candidateCount / limit);
  const instancesPerEdge = scatter ? Math.max(1, perElement) : 1;
  const count = elementCount * instancesPerEdge;

  const positions = new Float32Array(count * 3);
  let normals: Float32Array | null = null;
  let faceNormals: Float32Array | null = null;
  let edgeFaceMap: Map<number, number[]> | null = null;

  if (needNormals) {
    normals = new Float32Array(count * 3);
    faceNormals = computeFaceNormals(geo.positions, topo);
    edgeFaceMap = buildEdgeFaceMap(topo);
  }

  for (let i = 0; i < elementCount; i++) {
    const ei = candidates ? candidates[i * stride] : i * stride;
    const v0 = topo.edges[ei * 2];
    const v1 = topo.edges[ei * 2 + 1];

    for (let j = 0; j < instancesPerEdge; j++) {
      const idx = i * instancesPerEdge + j;
      const t = scatter ? seededRand(idx) : 0.5;

      positions[idx * 3] =
        geo.positions[v0 * 3] +
        t * (geo.positions[v1 * 3] - geo.positions[v0 * 3]);
      positions[idx * 3 + 1] =
        geo.positions[v0 * 3 + 1] +
        t * (geo.positions[v1 * 3 + 1] - geo.positions[v0 * 3 + 1]);
      positions[idx * 3 + 2] =
        geo.positions[v0 * 3 + 2] +
        t * (geo.positions[v1 * 3 + 2] - geo.positions[v0 * 3 + 2]);

      if (normals && faceNormals && edgeFaceMap) {
        const faces = edgeFaceMap.get(ei);
        let nx = 0,
          ny = 0,
          nz = 0;
        if (faces) {
          for (const fi of faces) {
            nx += faceNormals[fi * 3];
            ny += faceNormals[fi * 3 + 1];
            nz += faceNormals[fi * 3 + 2];
          }
        }
        const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
        if (len > 1e-10) {
          normals[idx * 3] = nx / len;
          normals[idx * 3 + 1] = ny / len;
          normals[idx * 3 + 2] = nz / len;
        } else {
          normals[idx * 3 + 2] = 1; // fallback: up
        }
      }
    }
  }

  return { positions, normals, count };
}

function extractFacePoints(
  geo: ModifierGeometry,
  limit: number,
  needNormals: boolean,
  scatter = false,
  perElement = 1,
  allowed?: Set<number>,
): PlacementPoints {
  const topo = geo.topology;
  if (!topo) return { positions: new Float32Array(0), normals: null, count: 0 };

  const faceCount = topo.offsets.length - 1;

  // Candidate face indices — when filtering, keep only faces whose every
  // corner references a vertex in the allowed set.
  let candidates: number[] | null = null;
  if (allowed) {
    candidates = [];
    for (let f = 0; f < faceCount; f++) {
      const start = topo.offsets[f];
      const end = topo.offsets[f + 1];
      let allIn = true;
      for (let c = start; c < end; c++) {
        if (!allowed.has(topo.corners[c])) {
          allIn = false;
          break;
        }
      }
      if (allIn) candidates.push(f);
    }
  }
  const candidateCount = candidates ? candidates.length : faceCount;
  const elementCount = Math.min(candidateCount, limit);
  const stride =
    candidateCount <= limit ? 1 : Math.ceil(candidateCount / limit);
  const instancesPerFace = scatter ? Math.max(1, perElement) : 1;
  const count = elementCount * instancesPerFace;

  const positions = new Float32Array(count * 3);
  let normals: Float32Array | null = null;
  let faceNormals: Float32Array | null = null;

  if (needNormals) {
    faceNormals = computeFaceNormals(geo.positions, topo);
    normals = new Float32Array(count * 3);
  }

  for (let i = 0; i < elementCount; i++) {
    const fi = candidates ? candidates[i * stride] : i * stride;
    const start = topo.offsets[fi];
    const end = topo.offsets[fi + 1];
    const faceSize = end - start;

    for (let j = 0; j < instancesPerFace; j++) {
      const idx = i * instancesPerFace + j;

      let px: number, py: number, pz: number;

      if (scatter && faceSize >= 3) {
        // Random point inside polygon via fan triangulation.
        // Three independent per-slot seeds: two for barycentric coords, one for
        // weighted triangle selection. Stride by 3 so no two slots share a seed.
        const base = idx * 3;
        const r1 = seededRand(base);
        const r2 = seededRand(base + 1);
        const r3 = seededRand(base + 2);

        const v0 = topo.corners[start];
        const x0 = geo.positions[v0 * 3],
          y0 = geo.positions[v0 * 3 + 1],
          z0 = geo.positions[v0 * 3 + 2];

        if (faceSize === 3) {
          // Triangle: use the sqrt trick for uniform sampling.
          const va = topo.corners[start + 1];
          const vb = topo.corners[start + 2];
          const sqr1 = Math.sqrt(r1);
          const w0 = 1 - sqr1;
          const w1 = sqr1 * (1 - r2);
          const w2 = sqr1 * r2;
          px =
            w0 * x0 + w1 * geo.positions[va * 3] + w2 * geo.positions[vb * 3];
          py =
            w0 * y0 +
            w1 * geo.positions[va * 3 + 1] +
            w2 * geo.positions[vb * 3 + 1];
          pz =
            w0 * z0 +
            w1 * geo.positions[va * 3 + 2] +
            w2 * geo.positions[vb * 3 + 2];
        } else {
          // General polygon: fan-triangulate from v0, pick triangle by area.
          const numTris = faceSize - 2;
          let totalArea = 0;
          const areas = new Float32Array(numTris);
          for (let t = 0; t < numTris; t++) {
            const va = topo.corners[start + t + 1];
            const vb = topo.corners[start + t + 2];
            const ax = geo.positions[va * 3] - x0,
              ay = geo.positions[va * 3 + 1] - y0,
              az = geo.positions[va * 3 + 2] - z0;
            const bx = geo.positions[vb * 3] - x0,
              by = geo.positions[vb * 3 + 1] - y0,
              bz = geo.positions[vb * 3 + 2] - z0;
            const cx = ay * bz - az * by,
              cy = az * bx - ax * bz,
              cz = ax * by - ay * bx;
            areas[t] = Math.sqrt(cx * cx + cy * cy + cz * cz) * 0.5;
            totalArea += areas[t];
          }
          const target = r3 * totalArea;
          let chosenTri = numTris - 1;
          let cumArea = 0;
          for (let t = 0; t < numTris; t++) {
            cumArea += areas[t];
            if (target <= cumArea) {
              chosenTri = t;
              break;
            }
          }
          const va = topo.corners[start + chosenTri + 1];
          const vb = topo.corners[start + chosenTri + 2];
          const sqr1 = Math.sqrt(r1);
          const w0 = 1 - sqr1;
          const w1 = sqr1 * (1 - r2);
          const w2 = sqr1 * r2;
          px =
            w0 * x0 + w1 * geo.positions[va * 3] + w2 * geo.positions[vb * 3];
          py =
            w0 * y0 +
            w1 * geo.positions[va * 3 + 1] +
            w2 * geo.positions[vb * 3 + 1];
          pz =
            w0 * z0 +
            w1 * geo.positions[va * 3 + 2] +
            w2 * geo.positions[vb * 3 + 2];
        }
      } else {
        // Centroid (default, and fallback for degenerate faces)
        let cx = 0,
          cy = 0,
          cz = 0;
        for (let c = start; c < end; c++) {
          const v = topo.corners[c];
          cx += geo.positions[v * 3];
          cy += geo.positions[v * 3 + 1];
          cz += geo.positions[v * 3 + 2];
        }
        px = cx / faceSize;
        py = cy / faceSize;
        pz = cz / faceSize;
      }

      positions[idx * 3] = px;
      positions[idx * 3 + 1] = py;
      positions[idx * 3 + 2] = pz;

      if (normals && faceNormals) {
        normals[idx * 3] = faceNormals[fi * 3];
        normals[idx * 3 + 1] = faceNormals[fi * 3 + 1];
        normals[idx * 3 + 2] = faceNormals[fi * 3 + 2];
      }
    }
  }

  return { positions, normals, count };
}

// ---------------------------------------------------------------------------
// Orientation matrix construction
// ---------------------------------------------------------------------------

function buildOrientationMatrix(
  px: number,
  py: number,
  pz: number,
  nx: number,
  ny: number,
  nz: number,
  orientation: "local" | "normal" | "global",
  ownerInverseRotation?: number[],
): number[] {
  if (orientation === "local") {
    return mat4Translation(px, py, pz);
  }

  if (orientation === "global") {
    if (!ownerInverseRotation) return mat4Translation(px, py, pz);
    // ownerInverseRotation cancels the owner's rotation so instances face world axes.
    // The rotation part is the upper-left 3×3 of the inverse; translation is the placement point.
    return [
      ownerInverseRotation[0],
      ownerInverseRotation[1],
      ownerInverseRotation[2],
      0,
      ownerInverseRotation[4],
      ownerInverseRotation[5],
      ownerInverseRotation[6],
      0,
      ownerInverseRotation[8],
      ownerInverseRotation[9],
      ownerInverseRotation[10],
      0,
      px,
      py,
      pz,
      1,
    ];
  }

  // orientation === "normal": build orthonormal basis with Z aligned to normal
  // Pick a reference vector that is not parallel to the normal
  let refX = 0,
    refY = 1,
    refZ = 0; // world Y
  if (Math.abs(ny) > 0.9) {
    refX = 1;
    refY = 0;
    refZ = 0; // world X
  }

  // bitangent = normalize(cross(normal, ref))
  let bx = ny * refZ - nz * refY;
  let by = nz * refX - nx * refZ;
  let bz = nx * refY - ny * refX;
  const len = Math.sqrt(bx * bx + by * by + bz * bz);
  if (len > 1e-10) {
    bx /= len;
    by /= len;
    bz /= len;
  }

  // tangent = cross(bitangent, normal)
  const tx = by * nz - bz * ny;
  const ty = bz * nx - bx * nz;
  const tz = bx * ny - by * nx;

  return mat4FromBasis(tx, ty, tz, bx, by, bz, nx, ny, nz, px, py, pz);
}

// ---------------------------------------------------------------------------
// Multi-instance geometry assembly
// ---------------------------------------------------------------------------

function emptyGeometry(): ModifierGeometry {
  return {
    positions: new Float32Array(0),
    indices: EMPTY_SHARED_TRIANGLE_INDICES,
    normals: null,
    uvs: null,
    morphArrays: [],
    skinIndex: null,
    skinWeight: null,
    sculptDisplacement: null,
    groups: [],
    edges: null,
    edgeFlags: null,
    skinRadii: null,
    provenance: identityProvenance(0),
  };
}

function scatterInstances(
  sourceGeo: ModifierGeometry,
  placements: PlacementPoints,
  orientation: "local" | "normal" | "global",
  ownerInverseRotation?: number[],
  jitterScale = 0,
  jitterRotation = 0,
): ModifierGeometry {
  const count = placements.count;
  if (count === 0) return emptyGeometry();

  const srcVertCount = sourceGeo.positions.length / 3;
  const srcIdxCount = sourceGeo.indices.length;
  const totalVerts = srcVertCount * count;
  const totalIndices = srcIdxCount * count;

  // Build orientation matrices, then apply jitter so hasRotation sees the
  // final state (jitter always introduces non-identity rotation/scale).
  //
  // Seed domains for jitter are kept far above the position-seed range
  // (~25000 max) by using high-bit offsets, so they never collide.
  //   Scale:    0x100000 | i
  //   Rotation: 0x200000 | i
  const mats: number[][] = [];
  for (let i = 0; i < count; i++) {
    const px = placements.positions[i * 3];
    const py = placements.positions[i * 3 + 1];
    const pz = placements.positions[i * 3 + 2];
    const nx = placements.normals ? placements.normals[i * 3] : 0;
    const ny = placements.normals ? placements.normals[i * 3 + 1] : 0;
    const nz = placements.normals ? placements.normals[i * 3 + 2] : 1;
    const mat = buildOrientationMatrix(
      px,
      py,
      pz,
      nx,
      ny,
      nz,
      orientation,
      ownerInverseRotation,
    );

    // Scale jitter: scale the three basis columns uniformly, leaving
    // translation (col 3) untouched.  Clamped to 0.01 to avoid degenerate/
    // inverted geometry.
    if (jitterScale > 0) {
      const s = Math.max(
        0.01,
        1 + (seededRand(0x100000 | i) - 0.5) * 2 * jitterScale,
      );
      mat[0] *= s;
      mat[1] *= s;
      mat[2] *= s;
      mat[4] *= s;
      mat[5] *= s;
      mat[6] *= s;
      mat[8] *= s;
      mat[9] *= s;
      mat[10] *= s;
    }

    // Rotation jitter: right-multiply by Rz(θ) to spin around the instance's
    // local Z axis (the placement normal in "normal" orientation, world Z
    // otherwise).  Only cols 0 & 1 change; col 2 (normal axis) is preserved.
    if (jitterRotation > 0) {
      const angle = seededRand(0x200000 | i) * Math.PI * 2 * jitterRotation;
      const cosA = Math.cos(angle);
      const sinA = Math.sin(angle);
      const c0x = mat[0] * cosA + mat[4] * sinA;
      const c0y = mat[1] * cosA + mat[5] * sinA;
      const c0z = mat[2] * cosA + mat[6] * sinA;
      const c1x = -mat[0] * sinA + mat[4] * cosA;
      const c1y = -mat[1] * sinA + mat[5] * cosA;
      const c1z = -mat[2] * sinA + mat[6] * cosA;
      mat[0] = c0x;
      mat[1] = c0y;
      mat[2] = c0z;
      mat[4] = c1x;
      mat[5] = c1y;
      mat[6] = c1z;
    }

    mats.push(mat);
  }

  // Check if any matrix has rotation (non-identity upper-left 3×3)
  const hasRotation = mats.some(
    (m) =>
      Math.abs(m[0] - 1) > 1e-6 ||
      Math.abs(m[1]) > 1e-6 ||
      Math.abs(m[2]) > 1e-6 ||
      Math.abs(m[4]) > 1e-6 ||
      Math.abs(m[5] - 1) > 1e-6 ||
      Math.abs(m[6]) > 1e-6 ||
      Math.abs(m[8]) > 1e-6 ||
      Math.abs(m[9]) > 1e-6 ||
      Math.abs(m[10] - 1) > 1e-6,
  );

  // Allocate output arrays
  const newPos = new Float32Array(totalVerts * 3);
  const hasNorms = sourceGeo.normals !== null;
  const newNorm = hasNorms ? new Float32Array(totalVerts * 3) : null;
  const hasUVs = sourceGeo.uvs !== null;
  const newUVs = hasUVs ? new Float32Array(totalVerts * 2) : null;
  const hasSkin = sourceGeo.skinIndex !== null && sourceGeo.skinWeight !== null;
  const newSkinIndex = hasSkin ? new Float32Array(totalVerts * 4) : null;
  const newSkinWeight = hasSkin ? new Float32Array(totalVerts * 4) : null;
  const hasSculpt = sourceGeo.sculptDisplacement !== null;
  const newSculpt = hasSculpt ? new Float32Array(totalVerts * 3) : null;
  const newMorphArrays = sourceGeo.morphArrays.map(
    () => new Float32Array(totalVerts * 3),
  );
  const newIndices = asSharedTriangleIndices(new Uint32Array(totalIndices));

  // Fill per-copy data
  for (let c = 0; c < count; c++) {
    const mat = mats[c];
    const posOut = c * srcVertCount * 3;

    // Positions
    for (let v = 0; v < srcVertCount; v++) {
      const src = v * 3;
      const dst = posOut + src;
      const [px, py, pz] = transformPoint(
        mat,
        sourceGeo.positions[src],
        sourceGeo.positions[src + 1],
        sourceGeo.positions[src + 2],
      );
      newPos[dst] = px;
      newPos[dst + 1] = py;
      newPos[dst + 2] = pz;
    }

    // Normals
    if (newNorm && sourceGeo.normals) {
      if (hasRotation) {
        for (let v = 0; v < srcVertCount; v++) {
          const src = v * 3;
          const dst = posOut + src;
          let [rnx, rny, rnz] = transformDir(
            mat,
            sourceGeo.normals[src],
            sourceGeo.normals[src + 1],
            sourceGeo.normals[src + 2],
          );
          const len = Math.sqrt(rnx * rnx + rny * rny + rnz * rnz);
          if (len > 1e-8) {
            rnx /= len;
            rny /= len;
            rnz /= len;
          }
          newNorm[dst] = rnx;
          newNorm[dst + 1] = rny;
          newNorm[dst + 2] = rnz;
        }
      } else {
        newNorm.set(sourceGeo.normals, posOut);
      }
    }

    // UVs — no spatial transform
    if (newUVs && sourceGeo.uvs) {
      newUVs.set(sourceGeo.uvs, c * srcVertCount * 2);
    }

    // Morph deltas — relative displacements, no transform
    for (let m = 0; m < sourceGeo.morphArrays.length; m++) {
      newMorphArrays[m].set(sourceGeo.morphArrays[m], posOut);
    }

    // Skin weights — no transform
    if (
      newSkinIndex &&
      newSkinWeight &&
      sourceGeo.skinIndex &&
      sourceGeo.skinWeight
    ) {
      newSkinIndex.set(sourceGeo.skinIndex, c * srcVertCount * 4);
      newSkinWeight.set(sourceGeo.skinWeight, c * srcVertCount * 4);
    }

    // Sculpt displacement — no transform
    if (newSculpt && sourceGeo.sculptDisplacement) {
      newSculpt.set(sourceGeo.sculptDisplacement, posOut);
    }

    // Indices — remap to this copy's vertex range
    const vertOffset = c * srcVertCount;
    const idxBase = c * srcIdxCount;
    for (let i = 0; i < srcIdxCount; i++) {
      newIndices[idxBase + i] = sourceGeo.indices[i] + vertOffset;
    }
  }

  // Duplicate material groups across copies
  const newGroups: GeometryGroup[] = [];
  if (sourceGeo.groups.length > 0) {
    for (let c = 0; c < count; c++) {
      const idxOffset = c * srcIdxCount;
      for (const g of sourceGeo.groups) {
        newGroups.push({
          start: g.start + idxOffset,
          count: g.count,
          materialIndex: g.materialIndex,
        });
      }
    }
  }

  // Replicate wire edges per copy. Creases travel alongside flags — each
  // scattered copy inherits its source weights verbatim.
  let newEdges: Uint32Array | null = null;
  let newEdgeFlags: Uint8Array | null = null;
  let newEdgeCreases: Float32Array | null = null;
  if (sourceGeo.edges && sourceGeo.edges.length > 0 && sourceGeo.edgeFlags) {
    const srcEdgeCount = sourceGeo.edges.length / 2;
    newEdges = new Uint32Array(srcEdgeCount * count * 2);
    newEdgeFlags = new Uint8Array(srcEdgeCount * count);
    const srcCreases = sourceGeo.edgeCreases ?? null;
    if (srcCreases) newEdgeCreases = new Float32Array(srcEdgeCount * count);
    for (let c = 0; c < count; c++) {
      const vertOff = c * srcVertCount;
      const edgeBase = c * srcEdgeCount;
      for (let i = 0; i < srcEdgeCount; i++) {
        newEdges[(edgeBase + i) * 2] = sourceGeo.edges[i * 2] + vertOff;
        newEdges[(edgeBase + i) * 2 + 1] = sourceGeo.edges[i * 2 + 1] + vertOff;
        newEdgeFlags[edgeBase + i] = sourceGeo.edgeFlags[i];
        if (newEdgeCreases && srcCreases)
          newEdgeCreases[edgeBase + i] = srcCreases[i];
      }
    }
  }

  // Replicate skin radii
  let newSkinRadii: Float32Array | null = null;
  if (sourceGeo.skinRadii) {
    newSkinRadii = new Float32Array(totalVerts);
    for (let c = 0; c < count; c++) {
      newSkinRadii.set(sourceGeo.skinRadii, c * srcVertCount);
    }
  }

  // Replicate polygon topology
  let newTopology: PolygonTopology | undefined;
  if (sourceGeo.topology) {
    const { offsets, corners } = sourceGeo.topology;
    const faceCount = offsets.length - 1;
    const srcCornerCount = corners.length;
    const newFaceCount = faceCount * count;
    const newCornerCount = srcCornerCount * count;

    const newOffsets = new Uint32Array(newFaceCount + 1);
    const newCorners = new Uint32Array(newCornerCount);

    for (let c = 0; c < count; c++) {
      const faceBase = c * faceCount;
      const cornerBase = c * srcCornerCount;
      const vertOffset = c * srcVertCount;
      for (let f = 0; f < faceCount; f++) {
        newOffsets[faceBase + f] = cornerBase + offsets[f];
      }
      for (let i = 0; i < srcCornerCount; i++) {
        newCorners[cornerBase + i] = corners[i] + vertOffset;
      }
    }
    newOffsets[newFaceCount] = newCornerCount;

    newTopology = buildTopologyWithExplicitEdges(
      newOffsets,
      newCorners,
      newEdges ?? new Uint32Array(0),
      newEdgeFlags ?? new Uint8Array(0),
      newEdgeCreases,
    );
    newEdges = newTopology.edges;
    newEdgeFlags = newTopology.edgeFlags;
    newEdgeCreases = newTopology.edgeCreases ?? null;
  }

  // Local provenance (source geo → scattered copies) — every copy vert has
  // its source vert as single parent. This is discarded by `instanceCore`
  // which builds a fresh owner-space provenance; it only exists so the
  // intermediate `ModifierGeometry` satisfies its type invariant.
  const localSrcForNew = new Int32Array(totalVerts);
  for (let c = 0; c < count; c++) {
    for (let v = 0; v < srcVertCount; v++) {
      localSrcForNew[c * srcVertCount + v] = v;
    }
  }
  const localProvenance = provenanceFromSourceMap(srcVertCount, localSrcForNew);

  return {
    positions: newPos,
    indices: newIndices,
    normals: newNorm,
    uvs: newUVs,
    morphArrays: newMorphArrays,
    skinIndex: newSkinIndex,
    skinWeight: newSkinWeight,
    sculptDisplacement: newSculpt,
    groups: newGroups,
    morphTargetsRelative: sourceGeo.morphTargetsRelative,
    morphNames: sourceGeo.morphNames,
    edges: newEdges,
    edgeFlags: newEdgeFlags,
    edgeCreases: newEdgeCreases,
    skinRadii: newSkinRadii,
    topology: newTopology,
    provenance: localProvenance,
  };
}

// ---------------------------------------------------------------------------
// Concatenation helper — glue owner geometry in front of scattered instances
// ---------------------------------------------------------------------------

/**
 * Concatenate two `ModifierGeometry` structs into a single output.
 *
 * Used by the Instance modifier when `showBase` is enabled so the owner
 * mesh is rendered alongside the scattered source copies.  Kept intentionally
 * private to this module — the modifier pipeline's currency is typed arrays
 * and unified concat lives only where it's meaningful.
 *
 * Indices, topology corners/edges, group starts, and wire edge pairs from
 * `b` are remapped by `a`'s vertex/index/corner/edge counts so the merged
 * result is a valid single-geometry struct.
 */
function concatModifierGeometry(
  a: ModifierGeometry,
  b: ModifierGeometry,
): ModifierGeometry {
  const aVerts = a.positions.length / 3;
  const bVerts = b.positions.length / 3;
  const totalVerts = aVerts + bVerts;
  const vertexOffset = aVerts;

  const positions = new Float32Array(totalVerts * 3);
  positions.set(a.positions, 0);
  positions.set(b.positions, a.positions.length);

  const indices = asSharedTriangleIndices(
    new Uint32Array(a.indices.length + b.indices.length),
  );
  indices.set(a.indices, 0);
  for (let i = 0; i < b.indices.length; i++) {
    indices[a.indices.length + i] = b.indices[i] + vertexOffset;
  }

  const concatStride = (
    aArr: Float32Array | null,
    bArr: Float32Array | null,
    stride: number,
  ): Float32Array | null => {
    if (!aArr && !bArr) return null;
    const out = new Float32Array(totalVerts * stride);
    if (aArr) out.set(aArr, 0);
    if (bArr) out.set(bArr, aVerts * stride);
    return out;
  };

  // Groups: copy `a`'s verbatim, then offset `b`'s starts by `a.indices.length`.
  const newGroups: GeometryGroup[] = [];
  for (const g of a.groups)
    newGroups.push({
      start: g.start,
      count: g.count,
      materialIndex: g.materialIndex,
    });
  for (const g of b.groups)
    newGroups.push({
      start: g.start + a.indices.length,
      count: g.count,
      materialIndex: g.materialIndex,
    });

  // Wire edges: concat with vertex offset. Creases follow the same
  // layout — allocated only when at least one side carries them.
  let newEdges: Uint32Array | null = null;
  let newEdgeFlags: Uint8Array | null = null;
  let newEdgeCreases: Float32Array | null = null;
  if ((a.edges && a.edges.length > 0) || (b.edges && b.edges.length > 0)) {
    const aEdgeCount = a.edges ? a.edges.length / 2 : 0;
    const bEdgeCount = b.edges ? b.edges.length / 2 : 0;
    newEdges = new Uint32Array((aEdgeCount + bEdgeCount) * 2);
    newEdgeFlags = new Uint8Array(aEdgeCount + bEdgeCount);
    const anyCrease = a.edgeCreases != null || b.edgeCreases != null;
    if (anyCrease) newEdgeCreases = new Float32Array(aEdgeCount + bEdgeCount);
    if (a.edges && a.edgeFlags) {
      newEdges.set(a.edges, 0);
      newEdgeFlags.set(a.edgeFlags, 0);
      if (newEdgeCreases && a.edgeCreases) newEdgeCreases.set(a.edgeCreases, 0);
    }
    if (b.edges && b.edgeFlags) {
      for (let i = 0; i < b.edges.length; i++) {
        newEdges[aEdgeCount * 2 + i] = b.edges[i] + vertexOffset;
      }
      newEdgeFlags.set(b.edgeFlags, aEdgeCount);
      if (newEdgeCreases && b.edgeCreases)
        newEdgeCreases.set(b.edgeCreases, aEdgeCount);
    }
  }

  // Topology: only preserved when both sides carry it (otherwise downstream
  // finalize reconstructs triangle topology from indices).
  let newTopology: PolygonTopology | undefined;
  if (a.topology && b.topology) {
    newTopology = concatTopology(a.topology, b.topology, vertexOffset);
    newEdges = newTopology.edges;
    newEdgeFlags = newTopology.edgeFlags;
    newEdgeCreases = newTopology.edgeCreases ?? null;
  }

  // Morph arrays: align by name where present, otherwise position-index.
  // The common case for Instance is that neither side has morphs, or only
  // the owner side does (source objects from scenes rarely carry shape keys).
  const morphArrays: Float32Array[] = [];
  const morphNames: string[] = [];
  if (a.morphArrays.length > 0 || b.morphArrays.length > 0) {
    const aNames = a.morphNames ?? a.morphArrays.map((_, i) => `__morph_${i}`);
    const bNames = b.morphNames ?? b.morphArrays.map((_, i) => `__morph_${i}`);
    const allNames = [...aNames];
    for (const n of bNames) if (!allNames.includes(n)) allNames.push(n);
    for (const name of allNames) {
      const out = new Float32Array(totalVerts * 3);
      const aIdx = aNames.indexOf(name);
      const bIdx = bNames.indexOf(name);
      if (aIdx >= 0) out.set(a.morphArrays[aIdx], 0);
      if (bIdx >= 0) out.set(b.morphArrays[bIdx], aVerts * 3);
      morphArrays.push(out);
      morphNames.push(name);
    }
  }

  // Local provenance for the concat: `a`'s verts at identity, `b`'s verts
  // novel with no parents in `a`'s space. `instanceCore` overrides with the
  // correct owner-space provenance at the top level.
  const aVertCount = a.positions.length / 3;
  const bVertCount = b.positions.length / 3;
  const concatSrcForNew = new Int32Array(aVertCount + bVertCount).fill(-1);
  for (let i = 0; i < aVertCount; i++) concatSrcForNew[i] = i;
  const concatProvenance = provenanceFromSourceMap(aVertCount, concatSrcForNew);

  return {
    positions,
    indices,
    normals:
      a.normals && b.normals ? concatStride(a.normals, b.normals, 3) : null,
    uvs: concatStride(a.uvs, b.uvs, 2),
    morphArrays,
    morphNames:
      morphArrays.length > 0 &&
      morphNames.some((n) => !n.startsWith("__morph_"))
        ? morphNames
        : undefined,
    morphTargetsRelative: a.morphTargetsRelative ?? b.morphTargetsRelative,
    skinIndex: concatStride(a.skinIndex, b.skinIndex, 4),
    skinWeight: concatStride(a.skinWeight, b.skinWeight, 4),
    sculptDisplacement: concatStride(
      a.sculptDisplacement,
      b.sculptDisplacement,
      3,
    ),
    groups: newGroups,
    edges: newEdges,
    edgeFlags: newEdgeFlags,
    edgeCreases: newEdgeCreases,
    skinRadii: concatStride(a.skinRadii, b.skinRadii, 1),
    topology: newTopology,
    provenance: concatProvenance,
  };
}

// ---------------------------------------------------------------------------
// Exported core function
// ---------------------------------------------------------------------------

export function instanceCore(
  geo: ModifierGeometry,
  mod: Extract<BlendModifier, { kind: "instance" }>,
  input?: ModifierEvaluationInput,
): ModifierGeometry {
  // Fallback when no source is assigned: honor `showBase` so the owner
  // mesh still renders instead of disappearing entirely.
  if (!mod.sourceObjectName) return mod.showBase ? geo : emptyGeometry();
  const sourceGeo = input?.sourceGeometries?.[mod.sourceObjectName];
  if (!sourceGeo) return mod.showBase ? geo : emptyGeometry();

  const placementMode = mod.placementMode ?? "origin";
  const orientation = mod.orientation ?? "local";
  const instanceLimit = mod.instanceLimit ?? 64;
  const needNormals = orientation === "normal";
  // Scatter is only meaningful for edge/face placement.
  const scatter =
    (mod.scatterMode ?? false) &&
    (placementMode === "edges" || placementMode === "faces");
  const perElement = Math.max(1, Math.round(mod.instancesPerElement ?? 1));
  const jitterScale =
    mod.jitterEnabled && placementMode !== "origin"
      ? Math.max(0, mod.jitterScale ?? 0)
      : 0;
  const jitterRotation =
    mod.jitterEnabled && placementMode !== "origin"
      ? Math.max(0, mod.jitterRotation ?? 0)
      : 0;

  const allowed =
    projectVertexGroupThroughProvenance(
      mod.vertexGroupIndices,
      geo.provenance,
    ) ?? undefined;

  let placements: PlacementPoints;

  switch (placementMode) {
    case "origin":
      placements = {
        positions: new Float32Array([0, 0, 0]),
        normals: null,
        count: 1,
      };
      break;
    case "vertices":
      placements = extractVertexPoints(
        geo,
        instanceLimit,
        needNormals,
        allowed,
      );
      // Fall back to origin if the filter eliminated every candidate.
      if (placements.count === 0) {
        placements = {
          positions: new Float32Array([0, 0, 0]),
          normals: null,
          count: 1,
        };
      }
      break;
    case "edges":
      placements = extractEdgePoints(
        geo,
        instanceLimit,
        needNormals,
        scatter,
        perElement,
        allowed,
      );
      // Fall back to origin if topology was unavailable or the filter was empty
      if (placements.count === 0) {
        placements = {
          positions: new Float32Array([0, 0, 0]),
          normals: null,
          count: 1,
        };
      }
      break;
    case "faces":
      placements = extractFacePoints(
        geo,
        instanceLimit,
        needNormals,
        scatter,
        perElement,
        allowed,
      );
      if (placements.count === 0) {
        placements = {
          positions: new Float32Array([0, 0, 0]),
          normals: null,
          count: 1,
        };
      }
      break;
  }

  const instances = scatterInstances(
    sourceGeo,
    placements,
    // Normal orientation has no meaning at origin (no surface)
    placementMode === "origin" && orientation === "normal"
      ? "local"
      : orientation,
    input?.ownerInverseRotation,
    jitterScale,
    jitterRotation,
  );

  // When showBase is enabled, render the owner mesh alongside the scattered
  // copies.  The owner comes first so its face/corner indices align with the
  // original topology, keeping material groups intact when they exist.
  const combined = mod.showBase
    ? concatModifierGeometry(geo, instances)
    : instances;

  // Rewrite provenance in OWNER's vertex space. The sub-helpers
  // (`scatterInstances`, `concatModifierGeometry`) produce local provenances
  // in their own sub-spaces (source-mesh or concat-a spaces) which aren't
  // meaningful to the modifier stack — the stack tracks ownerInput → output.
  //
  // When `showBase=true`, the owner occupies indices [0..ownerVertCount)
  // at identity and every scattered vert is novel (its parent lives in
  // another SceneObject's vertex space that owner-space can't reference).
  // When `showBase=false`, every output vert is novel; the owner is absent.
  const ownerVertCount = geo.positions.length / 3;
  const outVertCount = combined.positions.length / 3;
  const ownerSrcForNew = new Int32Array(outVertCount).fill(-1);
  if (mod.showBase) {
    for (let i = 0; i < ownerVertCount; i++) ownerSrcForNew[i] = i;
  }
  return {
    ...combined,
    provenance: provenanceFromSourceMap(ownerVertCount, ownerSrcForNew),
  };
}
